// src/api/stats.ts
import apiClient from "./base";

/**
 * Endpoint usati:
 *  - POST /users/fetch.php       -> { users: User[] }
 *  - POST /shapes/fetch.php      -> { shapes: Shape[] }
 *  - POST /pagamenti/fetch.php   -> { pagamenti: Payment[] } (ora da gettoni_movimenti)
 */

export type User = {
  id_utente: number;
  id_tipo_utente?: number; // 1=Admin, 3=Operatore, 5=Utente (adatta se diverso)
  nome: string;
  cognome: string;
  codice_fiscale: string;
  data_nascita: string;
  email: string;
  id_creatore?: number;
  created_at?: string;
  data_creazione?: string;
  timestamp_creazione?: string;
};

export type Shape = {
  id_shape: number;
  id_utente_analisi?: number;
  id_creatore?: number;
  nome?: string;
  cognome?: string;
  codice_fiscale?: string;
  data_nascita?: string;
  timestamp_creazione?: string;
  created_at?: string;
  data_creazione?: string;
  referto?: number;
};

/** Record pagamento dalla nuova API (lettura da gettoni_movimenti) */
export type Payment = {
  id_movimento?: number;
  id_pagamento: number | null;
  id_costo: number | null;

  id_utente: number;
  nome?: string;
  cognome?: string;
  nome_completo?: string;

  /** Importo in centesimi prelevato da gettoni_movimenti.amount_cents */
  prezzo: string | number;
  /** Compat FE: sempre "paid" */
  stato: "paid";
  /** timestamp_movimento della riga */
  timestamp_creazione: string;

  /** opzionale lato API: prezzo_eur normalizzato */
  prezzo_eur?: string;
};

type UsersFetchResponse = { users: User[] };
type ShapesFetchResponse = { shapes: Shape[] };
type PaymentsFetchResponse = { pagamenti: Payment[] };

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* ------------------------------ Utils ------------------------------ */

export function normalizeToLocaleDate(d?: string): string | null {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("it-IT");
}

/** Parsiamo “dd/mm/yyyy” -> Date (safe) */
function parseDDMMYYYY(s: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return isNaN(d.getTime()) ? null : d;
}

function inRangeInclusive(dateISO: string, startISO: string, endISO: string): boolean {
  const d = new Date(dateISO).getTime();
  const s = new Date(startISO).getTime();
  const e = new Date(endISO).getTime();
  return isFinite(d) && isFinite(s) && isFinite(e) && d >= s && d <= e;
}

/* ------------------------------ UTENTI ----------------------------- */

export async function getAllUsers(
  isAdmin: boolean,
  userId: number | null
): Promise<User[]> {
  const payload = isAdmin ? {} : { id_creatore: userId };
  const { data } = await apiClient.post<UsersFetchResponse>(
    "/users/fetch.php",
    payload,
    { headers: authHeader() }
  );
  return data.users ?? [];
}

export async function getUsersCount(
  isAdmin: boolean,
  userId: number | null
): Promise<number> {
  const users = await getAllUsers(isAdmin, userId);
  return users.length;
}

export async function getUsersByRole(
  isAdmin: boolean,
  userId: number | null
): Promise<Record<string, number>> {
  const users = await getAllUsers(isAdmin, userId);
  return users.reduce<Record<string, number>>((acc, u) => {
    const role = String(u.id_tipo_utente ?? "sconosciuto");
    acc[role] = (acc[role] ?? 0) + 1;
    return acc;
  }, {});
}

/* ------------------------------ SHAPES ----------------------------- */

export async function getAllShapes(
  isAdmin: boolean,
  userId: number | null
): Promise<Shape[]> {
  const payload = isAdmin ? {} : { id_creatore: userId };
  const { data } = await apiClient.post<ShapesFetchResponse>(
    "/shapes/fetch.php",
    payload,
    { headers: authHeader() }
  );
  return data.shapes ?? [];
}

export async function getShapesCount(
  isAdmin: boolean,
  userId: number | null
): Promise<number> {
  const shapes = await getAllShapes(isAdmin, userId);
  return shapes.length;
}

/* --------------------- PAGAMENTI / MOVIMENTI ----------------------- */

export type PaymentsQuery = {
  days?: number;
  startDate?: string; // ISO (YYYY-MM-DD) o parsabile
  endDate?: string;   // ISO
  id_utente?: number;
};

/** Chiamata grezza all’API (server: filtra per id_utente e, se implementato, days) */
export async function fetchPayments(params?: PaymentsQuery): Promise<Payment[]> {
  const payload: Record<string, any> = {};
  if (params?.days) payload.days = params.days;
  if (params?.id_utente) payload.id_utente = params.id_utente;

  const { data } = await apiClient.post<PaymentsFetchResponse>(
    "/pagamenti/fetch.php",
    payload,
    { headers: authHeader() }
  );

  let rows: Payment[] = data.pagamenti ?? [];

  // ✅ fallback client-side per DAYS (nel caso il BE non lo gestisse)
  if (typeof params?.days === "number" && params.days > 0) {
    const now = new Date();
    const min = new Date();
    min.setHours(0, 0, 0, 0);
    min.setDate(now.getDate() - (params.days - 1));

    rows = rows.filter((p) => {
      const d = new Date(p.timestamp_creazione);
      if (isNaN(d.getTime())) return false;
      d.setHours(0, 0, 0, 0);
      return d >= min && d <= now;
    });
  }

  // ✅ filtro client-side per range personalizzato
  if (params?.startDate && params?.endDate) {
    const startISO = new Date(params.startDate).toISOString();
    const endISO = new Date(params.endDate).toISOString();
    rows = rows.filter((p) => inRangeInclusive(p.timestamp_creazione, startISO, endISO));
  }

  return rows;
}

/** Totale (EUR) sul set ottenuto */
export async function getPaymentsTotalEuro(params?: PaymentsQuery): Promise<number> {
  const payments = await fetchPayments(params);
  const total = payments.reduce((sum, p) => {
    const cents = typeof p.prezzo === "string" ? parseInt(p.prezzo, 10) : p.prezzo;
    const eur = (isFinite(cents as number) ? Number(cents) : 0) / 100;
    return sum + eur;
  }, 0);
  return Number(total.toFixed(2));
}

/** Riga tabella per la UI */
export type PaymentRow = {
  data: string;         // dd/mm/yyyy
  id_utente: number;
  nome_completo: string;
  importo: number;      // in EUR
};

/** Dettagli transazioni (days / range) */
export async function getPaymentsDetailed(
  params?: PaymentsQuery
): Promise<PaymentRow[]> {
  const payments = await fetchPayments(params);

  const rows: PaymentRow[] = payments.map((p) => {
    const cents = typeof p.prezzo === "string" ? parseInt(p.prezzo, 10) : p.prezzo;
    const eur = (isFinite(cents as number) ? Number(cents) : 0) / 100;

    const nomeCompleto =
      (p as any).nome_completo ??
      ([p.nome, p.cognome].filter(Boolean).join(" ") || `Utente #${p.id_utente}`);

    return {
      data: normalizeToLocaleDate(p.timestamp_creazione) ?? "",
      id_utente: p.id_utente,
      nome_completo: nomeCompleto.trim(),
      importo: Number(eur.toFixed(2)),
    };
  });

  // Ordina per data desc (convertiamo dd/mm/yyyy -> Date per sicurezza)
  rows.sort((a, b) => {
    const da = parseDDMMYYYY(a.data)?.getTime() ?? 0;
    const db = parseDDMMYYYY(b.data)?.getTime() ?? 0;
    return db - da;
  });

  return rows;
}

/* ------------------ Wrapper legacy (se usati altrove) -------------- */

export async function getPaymentsTotalEuroLegacy(): Promise<number> {
  return getPaymentsTotalEuro();
}

export async function getPaymentsDetailedLastNDays(days: number): Promise<PaymentRow[]> {
  return getPaymentsDetailed({ days });
}
